import React, { useState } from 'react';
import {
  ChartBarIcon,
  BuildingOfficeIcon,
  Cog6ToothIcon,
  EnvelopeIcon,
  EyeIcon,
  ArrowRightOnRectangleIcon,
  PaperAirplaneIcon,
  DocumentTextIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import TenantDirectory from './tenants/TenantDirectory';
import OverviewDashboard from './overview/OverviewDashboard';
import UsageAnalytics from './analytics/UsageAnalytics';
import EmailTracking from './communications/EmailTracking';
import PlatformSettings from './configuration/PlatformSettings';
import InvitationsTab from '../admin/InvitationsTab';
import TemplatesManagement from './TemplatesManagement';
import BetaSignups from './beta/BetaSignups';

const PlatformDashboard = () => {
  const [activeModule, setActiveModule] = useState('overview');
  const [loading] = useState(false);
  const { logout, user } = useAuth();

  const modules = [
    {
      id: 'overview',
      name: 'Platform Overview',
      icon: EyeIcon,
      component: OverviewDashboard,
      description: 'Real-time platform metrics and health'
    },
    {
      id: 'tenants',
      name: 'Tenant Management', 
      icon: BuildingOfficeIcon,
      component: TenantDirectory,
      description: 'Manage organizations and view metrics'
    },
    {
      id: 'invitations',
      name: 'Client Invitations',
      icon: PaperAirplaneIcon,
      component: InvitationsTab,
      description: 'Send invitations to new clients'
    },
    {
      id: 'beta-signups',
      name: 'Beta Signups',
      icon: UserGroupIcon,
      component: BetaSignups,
      description: 'Manage beta access requests and signups'
    },
    {
      id: 'templates',
      name: 'Templates',
      icon: DocumentTextIcon,
      component: TemplatesManagement,
      description: 'Manage email templates and notifications'
    },
    {
      id: 'analytics',
      name: 'Analytics',
      icon: ChartBarIcon,
      component: UsageAnalytics,
      description: 'Usage analytics and insights'
    },
    {
      id: 'communications',
      name: 'Communications',
      icon: EnvelopeIcon,
      component: EmailTracking,
      description: 'Email and SMS tracking'
    },
    {
      id: 'configuration',
      name: 'Configuration',
      icon: Cog6ToothIcon,
      component: PlatformSettings,
      description: 'Platform settings and features'
    }
  ];

  const activeModuleData = modules.find(m => m.id === activeModule);
  const ActiveComponent = activeModuleData?.component;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Platform Administration</h1>
              <p className="text-sm text-gray-600">Condor Multi-Tenant Management Portal</p>
            </div>
            <div className="flex items-center space-x-4">
              <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-medium">
                Super Admin
              </span>
              <span className="text-sm text-gray-600">
                {user?.firstName} {user?.lastName}
              </span>
              <button
                onClick={logout}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <ArrowRightOnRectangleIcon className="h-4 w-4 mr-2" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Module Navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 overflow-x-auto">
            {modules.map((module) => {
              const Icon = module.icon;
              return (
                <button
                  key={module.id}
                  onClick={() => setActiveModule(module.id)}
                  className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                    activeModule === module.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="h-5 w-5 mr-2" />
                  {module.name}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Module Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-600 border-t-transparent"></div>
            <span className="ml-3 text-gray-600">Loading...</span>
          </div>
        ) : (
          <>
            {/* Module Description */}
            <div className="mb-6">
              <h2 className="text-lg font-medium text-gray-900 mb-1">
                {activeModuleData?.name}
              </h2>
              <p className="text-sm text-gray-600">
                {activeModuleData?.description}
              </p>
            </div>

            {/* Active Module Component */}
            {ActiveComponent && <ActiveComponent />}
          </>
        )}
      </div>
    </div>
  );
};

export default PlatformDashboard;